<template>
  <template v-if="display === 'grid'">
    <div class="product product--column shadow-base-narrow">
      <div class="product__preview">
        <a
          @click="handleClick"
          :href="product.link"
          :title="product.title"
          class="product__preview"
          tabindex="0"
        >
          <picture>
            <source :srcset="product.image.webp" type="image/webp" />
            <img
              :alt="product.title"
              :data-src="product.image.source"
              width="100"
              height="100"
              class="product__image lazyload"
              loading="lazy"
            />
          </picture>
        </a>

        <div class="product__btns product__btns--column">
          <add-to-wishlist-button
            class-name="btn--square z-[11]"
            :in18="in18"
            :product-id="product.id"
          />
          <add-to-compare-list-button
            class-name="btn--square z-[11]"
            :in18="in18"
            :product-id="product.id"
          />
        </div>

        <product-label-badge v-if="product.is_new" :options="product" :position="'tl'" :size="24" :content="in18['page_new']" class="text-[11px]"/>
        <product-label-badge v-else-if="product.is_featured" :options="product" :position="'tl'" :size="24" :content="in18['page_featured']" class="text-[11px]"/>
        <product-label-badge v-else-if="product.is_active_discount" :options="product" :position="'tl'" :size="24" :content="in18['page_discount']" class="text-[11px]"/>

      </div>

      <div class="product__code">
        <a @click="handleClick" :href="product.link" :title="product.title">
          {{ in18['page_product_code'] }} {{ product.code }}
        </a>
      </div>
      <h3 class="line-clamp-2 text-[14px] font-medium leading-[18px] flex-1">
        <a
          @click="handleClick"
          :href="product.link"
          :title="product.title"
          tabindex="0"
        >
          {{ product.title }}
        </a>
      </h3>

      <product-price class-name="product__price !mb-[16px]" :prices="product.prices" :color-variant="colorVariant" :in18="in18"/>

      <a
        v-if="product.has_variations"
        @click="handleClick"
        :href="product.link"
        :title="product.title"
        role="button"
        class="block"
        :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
      >
        {{ in18['page_open_parent_link_1'] }}
      </a>
      <add-to-cart-button
        v-else-if="product.quantity > 0 || product.virtual_quantity > 0"
        :in18="in18"
        :product-id="product.id"
        :title="in18['page_add_to_cart_1']"
        role="button"
        :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
      />
      <a
        v-else
        :href="product.link"
        @click="handleClick"
        :title="in18['page_unavailable_for_purchase_btn_1']"
        role="button"
        :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
      >
        {{ in18['page_unavailable_for_purchase_btn_1'] }}
      </a>
    </div>
  </template>

  <template v-else>
    <div class="product product--row">
      <div class="row">
        <div class="col-md-3">
          <a
            @click="handleClick"
            :href="product.link"
            :title="product.title"
            class="product__preview"
          >
            <picture>
              <source :srcset="product.image.webp" type="image/webp" />
              <img
                :alt="product.title"
                :data-src="product.image.source"
                width="100"
                height="100"
                class="lazyload"
              />
            </picture>
          </a>
        </div>

        <div class="col-md-9">
          <div class="product__header">
            <div class="product__code mb-2">
              <a @click="handleClick" :href="product.link" :title="product.title">
                {{ in18['page_product_code'] }} {{ product.code }}
              </a>
            </div>
            <h3 class="product__title">
              <a @click="handleClick" :href="product.link" :title="product.title">
                {{ product.title }}
              </a>
            </h3>
            <ul class="product__data">
              <li class="product_data-item">
                {{ in18['page_manufacturer'] }}
                <span class="text-primary fw-500">
                  {{ product.manufacture.title }}
                </span>
              </li>
              <li class="product_data-item">
                {{ in18['page_product_code'] }}
                <span class="fw-500">{{ product.code }}</span>
              </li>
            </ul>
          </div>
          <div class="product__text">
            <p>{{ product.short_description }}</p>
          </div>
          <div class="product__footer">
            <product-price
              :prices="product.prices"
              :color-variant="colorVariant"
              class-name="product__price mr-2"
            />
            <div class="product__btns flex flex-wrap justify-end gap-2">
              <add-to-wishlist-button :in18="in18" :product-id="product.id" />
              <add-to-compare-list-button :in18="in18" :product-id="product.id" />
              <a
                @click="handleClick"
                v-if="product.has_variations"
                :href="product.link"
                :title="product.title"
                role="button"
                :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
              >
                {{ in18['page_open_parent_link_1'] }}
              </a>
              <add-to-cart-button
                v-else-if="product.quantity > 0 || product.virtual_quantity > 0"
                :in18="in18"
                :product-id="product.id"
                :title="in18['page_add_to_cart_1']"
                :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
              />
              <a
                v-else
                @click="handleClick"
                :href="product.link"
                :title="in18['page_unavailable_for_purchase_btn_1']"
                role="button"
                :class="[ ' rounded-[10px] w-full py-[10px] px-[24px] text-center border border-charcoal text-[14px] text-charcoal transition-all hover:text-white hover:bg-charcoal']"
              >
                {{ in18['page_unavailable_for_purchase_btn_1'] }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup>
import { defineProps } from 'vue';

import AddToWishlistButton from './../product/AddToWishlistButton.vue';
import AddToCompareListButton from './../product/AddToCompareListButton.vue';
import AddToCartButton from "../product/AddToCartButton.vue";
import ProductLabelBadge from '../product/ProductLabelBadge.vue';

// Props
const props = defineProps({
  display: {
    type: String,
    default: 'grid',
  },
  product: {
    type: Object,
    required: true,
  },
  colorVariant: {
    type: String,
    default: 'green',
  }
});

// Access global translations
const in18 = window.in18['shop-product-item'];

// Methods
function handleClick() {
  ga_select_item_event(props.product);
  window.location.href = props.product.link;
}
</script>